import { extraErrorDataIntegration } from '@sentry/integrations'
import * as Sentry from '@sentry/react'
import { Extras } from '@sentry/types'
import {
  getAppVersion,
  isElectron,
  isProductionBuildEnvironment,
} from '~/utils/common'

enum SuppressionType {
  RegExp,
  ExactMatch,
  Contains,
  StartsWith,
}

interface MessageSuppression {
  value: string | RegExp
  type: SuppressionType
}

const errorMessagesToSuppress: Array<MessageSuppression> = [
  // We get a lot of WebSocket closed 1006 errors that are not actionable
  {
    value: /^WebSocket closed \S+:\d+ \(code: 1006\)$/,
    type: SuppressionType.RegExp,
  },
  // This error message is expected with Vite and can be ignored. It just means someone hasn't reloaded the page since a deploy and this will trigger a reload automatically
  {
    value: 'error loading dynamically imported module:',
    type: SuppressionType.StartsWith,
  },
  {
    value: 'ResizeObserver',
    type: SuppressionType.Contains,
  },
  {
    value: 'Failed to fetch',
    type: SuppressionType.StartsWith,
  },
]

export const shouldSuppressError = (message: string): boolean => {
  return errorMessagesToSuppress.some((messageToSuppress) => {
    switch (messageToSuppress.type) {
      case SuppressionType.RegExp:
        return (messageToSuppress.value as RegExp).test(message)
      case SuppressionType.ExactMatch:
        return message === messageToSuppress.value
      case SuppressionType.Contains:
        return message.includes(messageToSuppress.value as string)
      case SuppressionType.StartsWith:
        return message.startsWith(messageToSuppress.value as string)
    }
  })
}

function getDsn() {
  const developmentDsn =
    'https://f5f22fb6751b4ee582afaad0159d03d0@o372418.ingest.sentry.io/6167647'
  const productionDsn =
    'https://b010d3f8ae354228830c828729a220ca@o372418.ingest.sentry.io/5883539'
  const electronDevDsn =
    'https://c5f3f2b6042f38d7c8c9a6480c28659a@o372418.ingest.us.sentry.io/4507902346330112'
  const electronProdDsn =
    'https://42fd21c8ef4aad677d30fff3b38a3057@o372418.ingest.us.sentry.io/4507902350786560'

  if (isElectron) {
    return isProductionBuildEnvironment() ? electronProdDsn : electronDevDsn
  }

  return isProductionBuildEnvironment() ? productionDsn : developmentDsn
}

function getSessionSampleRate() {
  // https://docs.sentry.io/platforms/javascript/guides/electron/session-replay/#sampling
  // Guidance says 1.0 in dev and 0.1 in prods is a good rate.

  const developmentSampleRate = 0.0
  const productionSampleRate = 0.0
  const electronDevSampleRate = 0.0
  const electronProdSampleRate = 0.0

  if (isElectron) {
    return isProductionBuildEnvironment()
      ? electronProdSampleRate
      : electronDevSampleRate
  }

  return isProductionBuildEnvironment()
    ? productionSampleRate
    : developmentSampleRate
}

function getErrorSampleRate() {
  // https://docs.sentry.io/platforms/javascript/guides/electron/session-replay/#sampling
  // Guidance says 1.0 in dev and 0.1 in prods is a good rate.

  const developmentSampleRate = 0.0
  const productionSampleRate = 0.1
  const electronDevSampleRate = 0.0
  const electronProdSampleRate = 0.1

  if (isElectron) {
    return isProductionBuildEnvironment()
      ? electronProdSampleRate
      : electronDevSampleRate
  }

  return isProductionBuildEnvironment()
    ? productionSampleRate
    : developmentSampleRate
}

export const initiateSentry = () => {
  const doGetAppVersion = async (): Promise<string> => {
    return await getAppVersion()
  }

  doGetAppVersion().then((version: string) => {
    Sentry.init({
      dsn: getDsn(),
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
        }),
        extraErrorDataIntegration({
          depth: 9,
        }),
      ],
      normalizeDepth: 10,
      release: `${isElectron ? 'desktop-app' : 'web-app'}@${version}`,
      tracesSampleRate: 0,
      replaysSessionSampleRate: getSessionSampleRate(),
      replaysOnErrorSampleRate: getErrorSampleRate(),
      beforeSend: (event) => {
        if (event.exception) {
          const errorString = event.exception.values?.[0]?.value
          if (errorString && shouldSuppressError(errorString)) {
            return null
          }
        }

        return event
      },
    })
  })
}

export const captureException = (exception: unknown, extraData?: Extras) => {
  Sentry.captureException(exception, {
    extra: { ...extraData },
  })
}

export const addBreadCrumb = (breadCrumb: Sentry.Breadcrumb) => {
  Sentry.addBreadcrumb({
    level: breadCrumb.level ?? 'log',
    ...breadCrumb,
  })
}

export { Sentry }
